import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import * as bip39 from 'bip39';
import { PDFDocument, rgb } from 'pdf-lib';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import './CSS/Login.css';

const PdfHash = (props) => {
  const [hash, setHash] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState(0);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [showDownload, setShowDownload] = useState(false);
  const COST_PER_DOWNLOAD = 50;

  useEffect(() => {
    const state = props.location && props.location.state;
    if (state && state.email && state.balance !== undefined) {
      setEmail(state.email);
      setBalance(state.balance);
    } else {
      fetchUserData();
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const res = await fetch('/user-details');
      const data = await res.json();
      setEmail(data.email);
      setBalance(data.balance);
    } catch (err) {
      console.error('Error fetching user data', err);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (balance < COST_PER_DOWNLOAD) {
      setError("Not enough credits to generate the PDF.");
      return;
    }

    if (file && file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
        const sha256Hash = CryptoJS.SHA256(wordArray).toString(CryptoJS.enc.Hex);
        setHash(sha256Hash);
        setPdfFile(arrayBuffer);
        setPdfFileName(file.name);
        setCopySuccess('');
        setError('');

        try {
          const binaryHash = BigInt(`0x${sha256Hash}`).toString(2).padStart(256, '0');
          const first66Bits = binaryHash.slice(0, 66);
          const words = [];

          for (let i = 0; i < 6; i++) {
            const chunk = first66Bits.slice(i * 11, (i + 1) * 11);
            const index = parseInt(chunk, 2);
            words.push(bip39.wordlists.english[index]);
          }

          const mnemonicPhrase = words.join(' ');
          setMnemonic(mnemonicPhrase);

          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const pages = pdfDoc.getPages();

          pages.forEach((page) => {
            page.drawText(`SHA-256 Hash: ${sha256Hash}`, {
              x: 85,
              y: 404,
              size: 14,
              color: rgb(0, 0, 0),
            });
            page.drawText(`Mnemonic Phrase: ${mnemonicPhrase}`, {
              x: 85,
              y: 384,
              size: 14,
              color: rgb(0, 0, 0),
            });
          });

          const pdfBytesResult = await pdfDoc.save();
          setPdfBytes(pdfBytesResult);
          setShowDownload(true);
        } catch (error) {
          setError('Error generating mnemonic or preparing PDF.');
        }
      };
      reader.onerror = () => setError('Error reading file');
      reader.readAsArrayBuffer(file);
    } else {
      setError('Please upload a valid PDF file');
      setHash('');
      setMnemonic('');
    }
  };

  const handleDownload = async () => {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `modified_${pdfFileName}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const newBalance = balance - COST_PER_DOWNLOAD;
    await fetch('/update-balance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, newBalance })
    });
    setBalance(newBalance);
    setShowDownload(false);
  };

  const handleCopyCombined = () => {
    if (hash && mnemonic && pdfFileName) {
      const combined = `SHA-256 Hash: ${hash} | Mnemonic: ${mnemonic} | File Name: ${pdfFileName}`;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(combined)
          .then(() => setCopySuccess('Combined hash, mnemonic, and file name copied!'))
          .catch(() => setCopySuccess('Failed to copy.'));
      } else {
        setCopySuccess('Clipboard API not available in this environment.');
      }
    }
  };

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <img className="logo" src={logo} alt="Main Logo" />
      <h2>Upload PDF, Generate Hash, and Mnemonic</h2>
      <div style={{ marginBottom: '10px' }}>Email: {email} | Balance: {balance.toFixed(2)} credits</div>
      <input type="file" accept="application/pdf" onChange={handleFileChange} />
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {hash && (
        <div>
          <h3>Your SHA-256 Hash:</h3>
          <p>{hash}</p>
        </div>
      )}
      {mnemonic && (
        <div>
          <h3>Mnemonic Phrase:</h3>
          <p>{mnemonic}</p>
        </div>
      )}
      {hash && mnemonic && (
        <div style={{ marginTop: '20px' }}>
          <h3>Combined Hash, Mnemonic, and File Name:</h3>
          <p>{`SHA-256 Hash: ${hash} | Mnemonic: ${mnemonic} | File Name: ${pdfFileName}`}</p>
          <button
            onClick={handleCopyCombined}
            style={{
              backgroundColor: 'red',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            Please Copy Your Hash
          </button>
          {copySuccess && <p style={{ color: 'green' }}>{copySuccess}</p>}
        </div>
      )}
      {showDownload && (
        <div style={{ marginTop: '30px' }}>
          <p style={{ color: 'blue' }}><strong>✅ Ready to download your modified PDF. Please confirm:</strong></p>
          <button
            onClick={handleDownload}
            style={{
              backgroundColor: 'green',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Confirm & Download (−50 credits)
          </button>
        </div>
      )}
      <br /><br />
      <div>
        <a
          href="http://128.199.98.182:3000/login7"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            backgroundColor: 'red', // Red background for the button
            color: 'white', // White text
            padding: '15px 32px', // Padding for the button
            textAlign: 'center', // Center text
            textDecoration: 'none', // Remove underline
            borderRadius: '5px', // Rounded corners
            fontSize: '16px', // Font size
            display: 'inline-block', // Allow the button to behave like a block-level element
            cursor: 'pointer', // Cursor pointer to indicate clickability
            animation: 'flicker 1s infinite', // Apply flicker animation
          }}
        >
          Go to External Blockchain
        </a>
      </div>
      <br /><br />
      <div>Powered by</div>
      <img className='logo2' src={logo2} alt="Logo 2" />
      <div>New Wave Consulting Group</div>
    </div>
  );
};

export default withRouter(PdfHash);
